import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import materialApi from '../apiProvider/materialapi';
import currencyApi from '../apiProvider/currencyapi';
import './styles/productDetailsModal.css';

/**
 * A sub-component for rendering a single detail row.
 */
const DetailRow = ({ label, value }) => (
  <div className="row mb-2">
    <div className="col-md-4 text-muted fw-semibold">{label}</div>
    <div className="col-md-8">{value || '-'}</div>
  </div>
);

/**
 * A sub-component for rendering dynamic tables from attribute data with expandable rows.
 */
const DynamicAttributeTable = ({ title, data, materialMap, currencies, currencyMap }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  if (!data || !Array.isArray(data) || data.length === 0) return null;

  // Toggle row expansion
  const toggleRow = (index) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(index)) {
      newExpandedRows.delete(index);
    } else {
      newExpandedRows.add(index);
    }
    setExpandedRows(newExpandedRows);
  };

  // Collect all unique keys for the main table headers (excluding complex nested data)
  const mainKeys = new Set();
  data.forEach(item => {
    Object.keys(item).forEach(key => {
      // Filter out technical fields AND complex nested arrays (which will be in expansion)
      if (!['_id', '__v', 'id', 'materials', 'pricing'].includes(key)) {
        mainKeys.add(key);
      }
    });
  });

  const headers = Array.from(mainKeys);

  const formatHeader = (str) => {
    return str
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatValue = (val) => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    return String(val);
  };

  return (
    <div className="mt-4">
      <div className="text-secondary-light text-uppercase fw-bold border-bottom pb-8 mb-12 text-sm py-2">
        {title}
      </div>
      <div className="table-responsive scroll-sm" style={{ maxHeight: '420px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
        <table className="table table-hover table-bordered table-sm mt-0 mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr>
              <th style={{ width: '40px', position: 'sticky', top: 0, zIndex: 11, backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}></th>
              {headers.map((header) => (
                <th key={header} style={{ position: 'sticky', top: 0, zIndex: 11, backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  {formatHeader(header)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => {
              const hasMaterials = row.materials && Array.isArray(row.materials) && row.materials.length > 0;
              const hasPricing = row.pricing && Array.isArray(row.pricing) && row.pricing.length > 0;
              const isExpanded = expandedRows.has(idx);
              const canExpand = hasMaterials || hasPricing;

              return (
                <React.Fragment key={idx}>
                  <tr
                    onClick={() => canExpand && toggleRow(idx)}
                    style={{ cursor: canExpand ? 'pointer' : 'default' }}
                    className={isExpanded ? 'table-active' : ''}
                  >
                    <td className="text-center align-middle">
                      {canExpand && (
                        <Icon
                          icon={isExpanded ? "solar:alt-arrow-down-bold" : "solar:alt-arrow-right-bold"}
                          width="16"
                          style={{ color: isExpanded ? '#f6993f' : '#64748b' }}
                        />
                      )}
                    </td>
                    {headers.map((header) => (
                      <td key={`${header}-${idx}`} className="align-middle">
                        {formatValue(row[header])}
                      </td>
                    ))}
                  </tr>

                  {isExpanded && (
                    <tr>
                      <td colSpan={headers.length + 1} className="p-0 bg-light">
                        <div className="p-3">
                          {hasMaterials && (
                            <div className="mb-2">
                              <div className="text-xs fw-bold text-navy-800 text-uppercase mb-2 letter-spacing-1">
                                Material Variants
                              </div>
                              <table className="table table-sm table-bordered bg-white mb-0 shadow-sm">
                                <thead className="bg-light">
                                  <tr style={{ fontSize: '11px' }}>
                                    <th>Material</th>
                                    {/* <th>SKU</th> */}
                                    <th>Stock</th>
                                    {currencies.map(curr => (
                                      <th key={curr._id}>{curr.code} Price</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {row.materials.map((m, mIdx) => (
                                    <tr key={mIdx} style={{ fontSize: '11px' }}>
                                      <td className="fw-bold">
                                        {materialMap?.[m.material] || m.materialName || m.material || 'N/A'}
                                      </td>
                                      {/* <td>{m.sku || '-'}</td> */}
                                      <td>{m.stock ?? '-'}</td>
                                      {currencies.map(curr => {
                                        const priceObj = (m.pricing || []).find(p => p.currencyId === curr._id);
                                        return <td key={curr._id}>{priceObj ? `${curr.symbol} ${priceObj.price}` : '-'}</td>
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {!hasMaterials && hasPricing && (
                            <div className="mb-2">
                              <div className="text-xs fw-bold text-navy-800 text-uppercase mb-2 letter-spacing-1">
                                Pricing Details
                              </div>
                              <table className="table table-sm table-bordered bg-white mb-0 shadow-sm">
                                <thead className="bg-light">
                                  <tr style={{ fontSize: '11px' }}>
                                    {/* <th>SKU</th> */}
                                    {currencies.map(curr => (
                                      <th key={curr._id}>{curr.code} Price</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr style={{ fontSize: '11px' }}>
                                    {/* <td>{row.sku || '-'}</td> */}
                                    {currencies.map(curr => {
                                      const priceObj = (row.pricing || []).find(p => p.currencyId === curr._id);
                                      return <td key={curr._id}>{priceObj ? `${curr.symbol} ${priceObj.price}` : '-'}</td>
                                    })}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          )}

                          {/* Note: Pricing details are hidden by request */}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function ProductDetailsModal({ show, onHide, product }) {
  const [materialMap, setMaterialMap] = useState({});
  const [currencies, setCurrencies] = useState([]);
  const [currencyMap, setCurrencyMap] = useState({});

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        const res = await materialApi.materialList({ limit: 100 });
        if (res.status && res.response?.data) {
          const map = {};
          res.response.data.forEach(m => {
            map[m._id] = m.name;
          });
          setMaterialMap(map);
        }
      } catch (err) {
        console.error("Failed to fetch materials for modal mapping", err);
      }
    };

    const fetchCurrencies = async () => {
      try {
        const res = await currencyApi.getCurrencies({ limit: 100 });
        if (res.status && res.response?.data) {
          setCurrencies(res.response.data);
          const map = {};
          res.response.data.forEach(c => {
            map[c._id] = { code: c.code, symbol: c.symbol };
          });
          setCurrencyMap(map);
        }
      } catch (err) {
        console.error("Failed to fetch currencies for modal mapping", err);
      }
    };

    if (show) {
      fetchMaterials();
      fetchCurrencies();
    }
  }, [show]);

  const filteredCurrencies = useMemo(() => {
    if (!product || currencies.length === 0) return [];

    const usedCurrencyIds = new Set();
    const scanAttributes = (attrs) => {
      attrs?.forEach(attr => {
        // Normal variant pricing
        attr.pricing?.forEach(p => usedCurrencyIds.add(p.currencyId));
        // Material variant pricing
        attr.materials?.forEach(m => {
          m.pricing?.forEach(p => usedCurrencyIds.add(p.currencyId));
        });
      });
    };

    scanAttributes(product.customerAttribute);
    scanAttributes(product.wholesalerAttribute);

    return currencies.filter(c => usedCurrencyIds.has(c._id));
  }, [product, currencies]);

  if (!product) return null;

  const basicInfo = [
    { label: 'Product Name', value: product.productName },
    { label: 'Product Code', value: product.productCode },
    { label: 'Short Description', value: product.shortDescription },
    { label: 'Brand', value: product.brandName },
    { label: 'Category', value: product.categoryName },
    { label: 'Sub Category', value: product.subCategoryName },
    // { label: 'Child Category', value: product.childCategoryName },
    { label: 'Status', value: product.productStatus ? 'Active' : 'Inactive' },
    { label: 'Refundable', value: product.refundable ? 'Yes' : 'No' },
    { label: 'Tag / Label', value: product.tagAndLabel },
    { label: 'Low Stock Alert', value: product.lowStockAlert ? 'Yes' : 'No' },
    { label: 'Low Stock Quantity', value: product.lowStockQuantity },
  ];

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton className="bg-light border-bottom">
        <Modal.Title className="fw-bold">Product Details</Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-4 py-3">
        <div className="container-fluid">
          {/* Basic Product Info Section */}
          <div className="mb-4">
            <div className="text-secondary-light text-uppercase fw-bold border-bottom pb-8 mb-12 text-sm py-2">
              Product Overview
            </div>
            {basicInfo.map((info, idx) => (
              <DetailRow key={idx} label={info.label} value={info.value} />
            ))}
          </div>

          {/* Dynamic Attribute Tables Section */}
          <DynamicAttributeTable
            title="Customer Attributes (Variants / Sizes)"
            data={product.customerAttribute}
            materialMap={materialMap}
            currencies={filteredCurrencies}
            currencyMap={currencyMap}
          />
          <DynamicAttributeTable
            title="Wholesaler Attributes"
            data={product.wholesalerAttribute}
            materialMap={materialMap}
            currencies={filteredCurrencies}
            currencyMap={currencyMap}
          />
        </div>
      </Modal.Body>

      <Modal.Footer className="bg-light border-top-0">
        <Button variant="outline-secondary" onClick={onHide} className="px-4">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
